import { Injectable } from '@angular/core';

export interface ChisConsoleSocket {
  readyState: number;
  onopen: any;
  onerror: any;
  onclose: any;
  onmessage: any;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface ChisLoginContext {
  sessionId: string;
  companyCode: string;
  remember: boolean;
}

function createBridgeId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

@Injectable({ providedIn: 'root' })
export class ChisWebViewBridgeService {
  private readonly pageId = createBridgeId();
  private loginContext: ChisLoginContext | null = null;

  private get webView(): any {
    if (typeof window === 'undefined') {
      return null;
    }

    const hostWindow: any = window as any;
    return hostWindow.chrome && hostWindow.chrome.webview ? hostWindow.chrome.webview : null;
  }

  get isAvailable(): boolean {
    if (!this.webView || typeof window === 'undefined') {
      return false;
    }
    return (window as any).__chisConsoleBridgeProtocolVersion === 1;
  }

  get activeLoginSessionId(): string {
    return this.loginContext ? this.loginContext.sessionId : '';
  }

  getLoginContext(): ChisLoginContext | null {
    return this.loginContext;
  }

  setLoginContext(context: ChisLoginContext): void {
    if (!context || !context.sessionId || !context.companyCode) {
      return;
    }
    this.loginContext = context;
  }

  clearLoginContext(sessionId?: string): void {
    if (!sessionId || this.loginContext?.sessionId === sessionId) {
      this.loginContext = null;
    }
  }

  createConsoleSocket(): ChisConsoleSocket | null {
    if (!this.isAvailable) {
      return null;
    }
    return new ChisWebViewConsoleSocket(this, this.pageId);
  }

  listen(handler: (message: any) => void): () => void {
    const webView = this.webView;
    if (!webView) {
      return () => { };
    }

    const listener = (event: any) => handler(event.data);
    webView.addEventListener('message', listener);
    return () => webView.removeEventListener('message', listener);
  }

  postMessage(message: any): boolean {
    const webView = this.webView;
    if (!webView) {
      return false;
    }

    try {
      webView.postMessage(message);
      return true;
    } catch (ex) {
      return false;
    }
  }
}

class ChisWebViewConsoleSocket implements ChisConsoleSocket {
  readyState = 0;
  onopen: any;
  onerror: any;
  onclose: any;
  onmessage: any;

  private readonly clientId = createBridgeId();
  private removeListener: () => void;
  private handshakeTimer: any;
  private closeRaised = false;

  constructor(private bridge: ChisWebViewBridgeService, private pageId: string) {
    this.removeListener = bridge.listen(message => this.handleMessage(message));
    bridge.postMessage({
      type: 'console-bridge-hello',
      protocolVersion: 1,
      pageId: this.pageId,
      clientId: this.clientId
    });
    this.handshakeTimer = setTimeout(() => this.fail('等待主控台 bridge 握手逾時'), 10000);
  }

  send(data: string): void {
    if (this.readyState !== 1) {
      throw new Error('Console bridge 尚未連線');
    }
    if (!this.bridge.postMessage({
      type: 'console-request',
      pageId: this.pageId,
      clientId: this.clientId,
      payload: data
    })) {
      throw new Error('無法傳送 Console bridge 訊息');
    }
  }

  close(code?: number, reason?: string): void {
    if (this.readyState >= 2) {
      return;
    }
    this.readyState = 2;
    this.bridge.postMessage({
      type: 'console-bridge-close',
      pageId: this.pageId,
      clientId: this.clientId,
      code,
      reason
    });
    this.raiseClose(code || 1000, reason || '');
  }

  private handleMessage(message: any): void {
    if (!message || message.pageId !== this.pageId || message.clientId !== this.clientId) {
      return;
    }

    if (message.type === 'console-bridge-ready' && this.readyState === 0) {
      clearTimeout(this.handshakeTimer);
      this.readyState = 1;
      if (this.onopen) {
        this.onopen(new Event('open'));
      }
      return;
    }

    if (message.type === 'console-message' && this.readyState === 1) {
      if (this.onmessage) {
        this.onmessage(new MessageEvent('message', { data: message.payload }));
      }
      return;
    }

    if (message.type === 'console-transport-error') {
      this.fail(message.message || '主控台 bridge 連線失敗');
      return;
    }

    if (message.type === 'console-transport-closed') {
      this.raiseClose(message.code || 1006, message.reason || '主控台 bridge 已關閉');
    }
  }

  private fail(message: string): void {
    if (this.readyState >= 2) {
      return;
    }
    if (this.onerror) {
      this.onerror(new Event('error'));
    }
    this.raiseClose(1006, message);
  }

  private raiseClose(code: number, reason: string): void {
    if (this.closeRaised) {
      return;
    }
    clearTimeout(this.handshakeTimer);
    this.closeRaised = true;
    this.readyState = 3;
    this.bridge.postMessage({
      type: 'console-bridge-close',
      pageId: this.pageId,
      clientId: this.clientId,
      code,
      reason
    });
    if (this.removeListener) {
      this.removeListener();
      this.removeListener = null;
    }
    if (this.onclose) {
      this.onclose(new CloseEvent('close', { code, reason, wasClean: code !== 1006 }));
    }
  }
}
